// Wiring for the load-time sample. The shaping, the route pattern and the
// guards all live in perf-sample.ts (pure, tested); this file is the part that
// touches `window` and can only be verified by running the app.
//
// One sample per page load, taken after `load` has fired so every timing it
// reads is final. Fire-and-forget through error-monitor's transport.

import {
  APP_INTERACTIVE_MARK,
  buildPerfSample,
  isReportableSample,
  perfMessage,
  routePattern,
  type NavigationTimingLike,
} from './perf-sample';
import { sendTelemetry } from './error-monitor';

let reported = false;

/**
 * Mark the moment the app stops showing its loading gate.
 *
 * This is the number that actually matters to a user: `load` says the browser
 * finished fetching, but DashAlign shows "Loading DashAlign…" until auth and
 * the profile resolve, which is a separate several hundred ms to several
 * seconds on top. Safe to call more than once — only the first mark counts.
 */
export function markAppInteractive(): void {
  if (typeof performance === 'undefined' || !performance.mark) return;
  try {
    if (performance.getEntriesByName?.(APP_INTERACTIVE_MARK, 'mark').length) return;
    performance.mark(APP_INTERACTIVE_MARK);
  } catch {
    /* marks are a nicety, never a requirement */
  }
}

function readNavigation(): NavigationTimingLike | null {
  try {
    const [nav] = performance.getEntriesByType('navigation') as unknown as NavigationTimingLike[];
    return nav ?? null;
  } catch {
    return null;
  }
}

function readAppInteractive(): number | null {
  try {
    const [mark] = performance.getEntriesByName(APP_INTERACTIVE_MARK, 'mark');
    return mark ? mark.startTime : null;
  } catch {
    return null;
  }
}

function readResourceCount(): number | null {
  try {
    return performance.getEntriesByType('resource').length;
  } catch {
    return null;
  }
}

function report(): void {
  if (reported) return;
  reported = true;

  const sample = buildPerfSample({
    nav: readNavigation(),
    appInteractiveMs: readAppInteractive(),
    resourceCount: readResourceCount(),
  });

  if (!isReportableSample(sample)) return;

  void sendTelemetry(perfMessage(routePattern(window.location.pathname)), { perf: sample });
}

/**
 * Start collecting. Called once from main.tsx alongside initErrorMonitor.
 *
 * Production only, matching the error monitor: dev has no Supabase target
 * during unit tests or `vite preview`, and local timings would only pollute
 * the real distribution.
 */
export function initPerfMonitor(): void {
  if (typeof window === 'undefined' || typeof performance === 'undefined') return;
  if (import.meta.env.MODE === 'development') return;

  // `load` has already fired on a late init — take the sample now. Otherwise
  // wait for it, so loadEventEnd is populated rather than 0.
  if (document.readyState === 'complete') {
    // A tick later, so an app-interactive mark set in the same frame is seen.
    setTimeout(report, 0);
    return;
  }
  window.addEventListener('load', () => setTimeout(report, 0), { once: true });
}
